import React, { useEffect, useState, useCallback } from 'react';

import ReactGA from 'react-ga';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { create } from 'jss';
import rtl from 'jss-rtl';
import MomentUtils from '@date-io/moment';
import { SnackbarProvider, useSnackbar } from 'notistack';
import {
  createStyles,
  jssPreset,
  makeStyles,
  StylesProvider,
  ThemeProvider
} from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import Auth from 'src/components/Auth';
import CookiesNotification from 'src/components/CookiesNotification';
//import SettingsNotification from 'src/components/SettingsNotification';
//import GoogleAnalytics from 'src/components/GoogleAnalytics';
import ScrollReset from 'src/components/ScrollReset';
import useSettings from 'src/hooks/useSettings';
import { createTheme, ThemeConfigurator } from 'src/theme';
import Routes from 'src/Routes';
//import RoutesDev from 'src/RoutesDev';

import withClearCache from './ClearCache';
import { useDispatch, useSelector } from 'react-redux';
import { themeRegister } from './actions/themeActions';
import axios from 'axios';
import { themeConfig } from './theme/constans';
import { VERSION_APP, VERSION_CACHE, VERSION_CODE } from './constants';
import SplashScreen from './components/SplashScreen';
import { handleGetStorage } from './hooks/validationParticipantes';
import { handdleGetStorage, handdleSetStorage } from './hooks/useSocioValidacion';
import getOS from './hooks/getOS';
import LoadingScreen from './components/LoadingScreen';
import { atob } from 'buffer';


const history = createBrowserHistory();
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });

// Initialize google analytics page view tracking
ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID); // Aqui pones tu identificador de cuenta de Google Analytics

history.listen(location => {
  ReactGA.set({ page: location.pathname }); // Update the user's current page
  ReactGA.pageview(location.pathname); // Record a pageview for the given page
});

const useStyles = makeStyles(() =>
  createStyles({
    '@global': {
      '*': {
        boxSizing: 'border-box',
        margin: 0,
        padding: 0
      },
      html: {
        '-webkit-font-smoothing': 'antialiased',
        '-moz-osx-font-smoothing': 'grayscale',
        height: '100%',
        width: '100%'
      },
      body: {
        height: '100%',
        width: '100%'
      },
      '#root': {
        height: '100%',
        width: '100%'
      }
    }
  })
);

// const ClearCacheComponent = withClearCache();
// Función mejorada para limpiar cache y recargar la aplicación
// Maneja diferentes tipos de cache y contextos (WebView vs navegador)

const refreshCacheAndReload = async () => {
  try {
    // Se guardan las dos versiones en variables diferentes
    const currentVersion = localStorage.getItem('versionCache');
    const newVersion = VERSION_CACHE.appVersion;
    
    console.log("Verificando versión de cache:", { currentVersion, newVersion });
    
    // Si las versiones coinciden, no hacer nada
    if (currentVersion === newVersion && currentVersion !== null) {
      console.log("Cache actualizado, no se requiere limpieza");
      return;
    }

    console.log("Iniciando limpieza de cache...");
    
    // 1. Guardar nueva versión ANTES del reload para evitar loops infinitos
    localStorage.setItem("versionCache", newVersion);
    
    // 2. Limpiar Service Worker caches (solo si está disponible)
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        console.log("Eliminando caches:", cacheNames);
        
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log("Service Worker caches eliminados");
      } catch (cacheError) {
        console.warn("Error al eliminar Service Worker caches:", cacheError);
      }
    }
    
    // 3. Limpiar cache del navegador y recargar
    console.log("Recargando aplicación sin cache");
    
    // Método moderno para recargar sin cache
    if ('serviceWorker' in navigator) {
      // Desregistrar service workers activos
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => registration.unregister());
      });
    }
    
    // Forzar recarga completa
    window.location.href = window.location.href;
    
  } catch (error) {
    console.error("Error durante limpieza de cache:", error);
    
    // Fallback: recarga simple si todo falla
    try {
      localStorage.setItem("versionCache", VERSION_CACHE.appVersion);
      window.location.href = window.location.href;
    } catch (fallbackError) {
      console.error("Error en fallback:", fallbackError);
    }
  }
};




function MainApp() {
  useStyles();
  const dispatch = useDispatch();
  const [themeConfigRemote, setThemeConfigRemote] = useState(null);
  const { settings } = useSettings();
  const notistackRef = React.createRef();
  
  // Validación segura para evitar errores cuando themeConfig es null
  const getUseLegacyBiometricLogic = () => {
    try {
      const themeConfig = localStorage.getItem("themeConfig");
      if (themeConfig) {
        const parsedConfig = JSON.parse(themeConfig);
        return parsedConfig?.useLegacyBiometricLogic ?? true;
      }
      return true; // Valor por defecto si no existe themeConfig
    } catch (error) {
      console.error("Error al parsear themeConfig:", error);
      return true; // Valor por defecto en caso de error
    }
  };

  const getIsIphoneAppUpdated = () => {
    try {
      const themeConfig = localStorage.getItem("themeConfig");
      if (themeConfig) {
        const parsedConfig = JSON.parse(themeConfig);
        return parsedConfig?.isIphoneAppUpdated ?? false;
      }
      return false; // Valor por defecto si no existe themeConfig
    } catch (error) {
      console.error("Error al parsear themeConfig:", error);
      return false; // Valor por defecto en caso de error
    }
  };

  const getIsAndroidAppUpdated = () => {
    try {
      const themeConfig = localStorage.getItem("themeConfig");
      if (themeConfig) {
        const parsedConfig = JSON.parse(themeConfig);
        return parsedConfig?.isAndroidAppUpdated ?? false;
      }
      return false; // Valor por defecto si no existe themeConfig
    } catch (error) {
      console.error("Error al parsear themeConfig:", error);
      return false; // Valor por defecto en caso de error
    }
  };
  
  const useLegacyBiometricLogic = getUseLegacyBiometricLogic();
  const isIphoneAppUpdated = getIsIphoneAppUpdated();
  const isAndroidAppUpdated = getIsAndroidAppUpdated();

  /**
   * Funcion para manejar mensajes del WebView.
   * 
   * Recibe la respuesta del dispositivo nativo y guarda el deviceId en localStorage.
   */
  const handleMessageLegacy = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);

       console.log("handleMessage", data);
      if (data) {
        
        localStorage.setItem('deviceId', JSON.stringify({
          codigo: window.btoa(data?.codigo || `APP_${themeConfigRemote?.code}`),
          imei: window.btoa(data?.imei || `${data?.deviceId}` || `APP_${themeConfigRemote?.code}`),
          modelo: window.btoa(data?.modelo || `APP_${themeConfigRemote?.code}`),
          os: window.btoa(data?.os || `APP_${themeConfigRemote?.code}`),
          osVersion: window.btoa(data?.osVersion || `APP_${themeConfigRemote?.code}`)
        }));
        getOS() === 'Android' ? document.removeEventListener("message", handleMessageLegacy) : window.removeEventListener("message", handleMessageLegacy);
      }

    } catch (error) {
      console.error('Error en handleMessageLegacy:', error);
      getOS() === 'Android' ? document.removeEventListener("message", handleMessageLegacy) : window.removeEventListener("message", handleMessageLegacy);
    }
  }, [themeConfigRemote])

  const handleMessageNew = useCallback((event) => {
    try {
      const data = JSON.parse(event.data);
      console.log("handleMessageNew", data);
      if (data) {
        
        localStorage.setItem('deviceId', JSON.stringify({
          codigo: window.btoa(data?.codigo),
          imei: window.btoa(data?.imei),
          modelo: window.btoa(data?.modelo),
          os: window.btoa(data?.os),
          osVersion: window.btoa(data?.osVersion)
        }));
        getOS() === 'Android' ? document.removeEventListener("message", handleMessageNew) : window.removeEventListener("message", handleMessageNew);
      }
    } catch (error) {
      console.error('Error en handleMessageNew:', error);
      getOS() === 'Android' ? document.removeEventListener("message", handleMessageNew) : window.removeEventListener("message", handleMessageNew);
    }
  }, [])
  /**
   * Funcion para obtener el Id del dispositivo.
   * 
   * Ayuda a setear el Id del dispositivo en el localStorage para las peticiones e identificacion del usuario con su dispositivo seguro.
   * 
   * Obs: Se valida si el dispositivo es Android o IOS para agregar el evento de escucha del mensaje.
   * 
   * Obs: Se agrega el evento de escucha del mensaje para obtener el Id del dispositivo.
   * 
   * Obs: Se envia el mensaje al WebView para obtener el Id del dispositivo.
   * 
   * Obs: Se guarda el Id del dispositivo en el localStorage.
   * 
   * Obs: Si el dispositivo es un navegador web se utiliza la funcion de userAgent para obtener el Id del dispositivo.
   */
  const handleGetDeviceId = useCallback(() => {
    // Función para verificar y limpiar datos del dispositivo si contienen APP_
    const verifyAndCleanDeviceData = () => {
      try {
        const deviceId = localStorage.getItem('deviceId');
        if (deviceId) {
          const deviceData = JSON.parse(deviceId);
          
          // Desencriptar los campos usando atob
          const codigoDecrypted = atob(deviceData.codigo || '');
          const modeloDecrypted = atob(deviceData.modelo || '');
          const osDecrypted = atob(deviceData.os || '');
          const osVersionDecrypted = atob(deviceData.osVersion || '');
          
          // Verificar si todos los campos contienen "APP_"
          const allFieldsContainAPP = codigoDecrypted.includes('APP_') &&
                                      modeloDecrypted.includes('APP_') &&
                                      osDecrypted.includes('APP_') &&
                                      osVersionDecrypted.includes('APP_');
          
          if (allFieldsContainAPP) {
            console.log('Todos los campos contienen APP_, eliminando datos del dispositivo...');
            
            // Eliminar objetos del localStorage solo si existen
            if (localStorage.getItem('deviceId')) {
              localStorage.removeItem('deviceId');
              console.log('deviceId eliminado');
            }
            
            if (localStorage.getItem('biometric')) {
              localStorage.removeItem('biometric');
              console.log('biometric eliminado');
            }
            
            if (localStorage.getItem('faceid')) {
              localStorage.removeItem('faceid');
              console.log('faceid eliminado');
            }
            
            console.log('Proceso de limpieza completado');
          }
        }
      } catch (error) {
        console.error('Error al verificar y limpiar datos del dispositivo:', error);
      }
    };

    // Ejecutar la verificación ANTES de definir messageHandler
    verifyAndCleanDeviceData();
    
    

    // Choose the correct handler based on legacy logic flag
    const messageHandler = useLegacyBiometricLogic ? handleMessageLegacy : handleMessageNew;

    try {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'getDeviceId', data: null }));
      } else {
        localStorage.setItem('deviceId', JSON.stringify({
          codigo: window.btoa("WEB"),
          imei: window.btoa(navigator.userAgent.toString()),
          modelo: window.btoa("WEB"),
          os: window.btoa("WEB"),
          osVersion: window.btoa("WEB")
        }));
      }
    } catch (error) {
      console.error('Error en getDeviceId:', error);
    }

    if (window.ReactNativeWebView) {
      getOS() === 'Android' ? document.addEventListener("message", messageHandler) : window.addEventListener("message", messageHandler);
    }
  }, [handleMessageLegacy, handleMessageNew])

  const handleVersionApp = () => {
    if (window.ReactNativeWebView && getOS() === 'Android' && isAndroidAppUpdated) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'versionApp', data: VERSION_APP, versionWeb: VERSION_CODE }));
    }
    else if(isIphoneAppUpdated){
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'versionApp', data: VERSION_APP, versionWeb: VERSION_CODE }));
    }
  }

  useEffect(() => {
    async function fetchThemeConfig() {
      try {
        const themeConfig = await handleGetStorage();

        if (!themeConfig || themeConfig.error) { //  Si `themeConfig` falla, seteamos error
          throw new Error("No se pudo cargar la configuración.");
        }

        setThemeConfigRemote(themeConfig);
        handleGetDeviceId();
        handleVersionApp();
        refreshCacheAndReload();
      } catch (error) {
        console.log("Error al obtener el themeConfig", error);
        setThemeConfigRemote("error"); //  Guardamos "error" para mostrar pantalla de error
      }
    }

    fetchThemeConfig();
}, []);

  
return (
  themeConfigRemote === null ? <SplashScreen /> :
  themeConfigRemote === "error" ? (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '50px 40px',
        maxWidth: '450px',
        width: '100%',
        textAlign: 'center',
        border: '1px solid #e9ecef',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
      }}>
        {/* Icono de conexión perdida */}
        {/* <div style={{
          width: '60px',
          height: '60px',
          backgroundColor: '#dc3545',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 30px'
        }}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10.29 3.86L1.82 18A2 2 0 0 0 3.24 21h16.52a2 2 0 0 0 1.42-3.14L12.71 3.86a2 2 0 0 0-2.42 0z" 
                  stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 9v4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 17h.01" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div> */}

        {/* Título principal */}
        <h1 style={{
          fontSize: '22px',
          fontWeight: '600',
          color: '#212529',
          marginBottom: '15px',
          lineHeight: '1.4'
        }}>
          ¡Conexión perdida!
        </h1>

        {/* Mensaje descriptivo */}
        <p style={{
          fontSize: '16px',
          color: '#6c757d',
          marginBottom: '35px',
          lineHeight: '1.6',
          maxWidth: '350px',
          margin: '0 auto 35px'
        }}>
          Se ha producido una pérdida de conexión. Por favor, recarga la aplicación para continuar.
        </p>

        {/* Botón de recarga mejorado */}
        <button 
          onClick={() => window.location.reload()}
          onMouseOver={(e) => {
            e.target.style.backgroundColor = '#0056b3';
            e.target.style.transform = 'translateY(-1px)';
          }}
          onMouseOut={(e) => {
            e.target.style.backgroundColor = '#007bff';
            e.target.style.transform = 'translateY(0)';
          }}
          style={{
            backgroundColor: '#007bff',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '16px',
            fontWeight: '500',
            padding: '12px 32px',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            margin: '0 auto 25px',
            minWidth: '150px'
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4 4V9H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20 20V15H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L4 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3.51 15A9 9 0 0 0 18.36 18.36L20 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Recargar aplicación
        </button>

        {/* Mensaje adicional */}
        {/* <p style={{
          fontSize: '14px',
          color: '#868e96',
          marginTop: '15px'
        }}>
          Si el problema persiste, verifica tu conexión a internet
        </p> */}
      </div>
    </div>
  ) :
  
  <ThemeProvider theme={createTheme({settings,themeConfigRemote})}>
    <StylesProvider jss={jss}>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <SnackbarProvider
          ref={notistackRef}
          maxSnack={4}
          onClose={(event, reason) => {
            if (reason === 'clickaway') {
              notistackRef.current.closeSnackbar();
            }
          }}
          autoHideDuration={20000}
        >
          <Router history={history}>
            <Auth>
              <ScrollReset />
              {/*<GoogleAnalytics />*/}
              <CookiesNotification />
              {/*<SettingsNotification />*/}
              <Routes />
            </Auth>
          </Router>
        </SnackbarProvider>
      </MuiPickersUtilsProvider>
    </StylesProvider>
  </ThemeProvider>
);

}

function App() {
  return <MainApp/>;
}

export default App;